import React from "react";
import { getStartOfWeek, getEndOfWeek } from "utils/dateTime";
import { toDateKey, excursionOverlapsHour } from "../../shared/utils/planningUtils";
import { useExcursion } from "contexts/api";
import type { Excursion } from "api/excursions/Excursion.typed";
import type { ViewMode } from "components/common/PlanningCalendar";

const EMPTY_EXCURSIONS: Excursion[] = [];

export function useExcursionData(viewMode: ViewMode, currentDate: Date) {
  const { listExcursions } = useExcursion();

  const [excursions, setExcursions] = React.useState<Excursion[]>([]);
  const [loading, setLoading] = React.useState(false);

  const periodBounds = React.useMemo(() => {
    const startOfPeriod =
      viewMode === "month"
        ? new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
        : viewMode === "day"
          ? new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())
          : getStartOfWeek(currentDate);
    const endOfPeriod =
      viewMode === "month"
        ? new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
        : viewMode === "day"
          ? new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())
          : getEndOfWeek(currentDate);
    return { from: toDateKey(startOfPeriod), to: toDateKey(endOfPeriod) };
  }, [currentDate, viewMode]);

  const loadExcursions = React.useCallback(async () => {
    setLoading(true);
    const list = await listExcursions(periodBounds.from, periodBounds.to);
    const next = Array.isArray(list) ? list : [];
    setExcursions(next);
    setLoading(false);
  }, [periodBounds, listExcursions]);

  const silentReload = React.useCallback(async () => {
    const list = await listExcursions(periodBounds.from, periodBounds.to);
    const next = Array.isArray(list) ? list : [];
    setExcursions(next);
  }, [periodBounds, listExcursions]);

  React.useEffect(() => {
    loadExcursions();
  }, [loadExcursions]);

  const excursionsByDateKey = React.useMemo(() => {
    const m = new Map<string, Excursion[]>();
    for (const ex of excursions) {
      const key = ex.date.slice(0, 10);
      const list = m.get(key);
      if (list) list.push(ex);
      else m.set(key, [ex]);
    }
    return m;
  }, [excursions]);

  const getExcursionsForDay = React.useCallback(
    (day: Date): Excursion[] => excursionsByDateKey.get(toDateKey(day)) ?? EMPTY_EXCURSIONS,
    [excursionsByDateKey],
  );

  const getExcursionsForSlot = React.useCallback(
    (day: Date, hour: number): Excursion[] =>
      excursions.filter((ex) => excursionOverlapsHour(ex, day, hour)),
    [excursions],
  );

  return {
    excursions,
    setExcursions,
    loading,
    loadExcursions,
    silentReload,
    getExcursionsForDay,
    getExcursionsForSlot,
    periodBounds,
  };
}
